import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, notification } from "antd";
import Logo from "../../assets/image/logo_light.png";
import "../loginscreen/styles.css";
import { POST } from "../../utils/apirequest";
import { API } from "../../config/API";

const passwordRules = [
  { label: "Minimum 8 characters", test: (v: string) => v.length >= 8 },
  { label: "At least one lowercase letter", test: (v: string) => /[a-z]/.test(v) },
  { label: "At least one uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "At least one number", test: (v: string) => /\d/.test(v) },
  { label: "At least one special character", test: (v: string) => /[!@#$%^&*()_+[\]{};':"\\|,.<>?]/.test(v) },
];

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"email" | "otp" | "password">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [passwordValue, setPasswordValue] = useState("");

  const allPasswordRulesPassed = passwordValue
    ? passwordRules.every((r) => r.test(passwordValue))
    : false;

  const validatePassword = (_: any, value: any) => {
    if (!value) return Promise.reject("Please input your password!");
    if (passwordRules.every((r) => r.test(value))) return Promise.resolve();
    return Promise.reject("");
  };

  const handleSendOTP = async (values: any) => {
    try {
      setLoading(true);
      const response: any = await POST(API.ADMIN_FORGOT_PASSWORD, {
        email: values.email,
      });
      if (response.status === 200) {
        setEmail(values.email);
        setStep("otp");
        notification.success({
          message: "OTP Sent",
          description: "Please check your email for the OTP",
        });
      } else if (response.status === 403) {
        notification.error({
          message: "Account Blocked",
          description: response.message,
        });
      } else {
        notification.error({
          message: "Error",
          description: response.message || "Something went wrong",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (values: any) => {
    try {
      setLoading(true);
      const response: any = await POST(API.ADMIN_VERIFY_RESET_OTP, {
        email,
        otp: values.otp,
      });
      if (response.status === 200) {
        setOtp(values.otp);
        setStep("password");
        notification.success({
          message: "OTP Verified",
          description: "Please enter your new password",
        });
      } else {
        notification.error({
          message: "Error",
          description: response.message || "Invalid OTP",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (values: any) => {
    try {
      setLoading(true);
      const response: any = await POST(API.ADMIN_RESET_PASSWORD, {
        email,
        otp,
        newPassword: values.newPassword,
      });
      if (response.status === 200) {
        notification.success({
          message: "Success",
          description: "Password reset successfully. Please login.",
        });
        navigate("/");
      } else {
        notification.error({
          message: "Error",
          description: response.message || "Failed to reset password",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const stepTitle = {
    email: "Forgot Password",
    otp: "Verify OTP",
    password: "Reset Password",
  };

  const stepDescription = {
    email: "Enter your email to receive an OTP",
    otp: "Enter the OTP sent to your email",
    password: "Enter your new password",
  };

  return (
    <div className="loginSection">
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          padding: "0 20px",
        }}
      >
        <div className="loginSection-box">
          <div className="loginSection-logobox">
            <img src={Logo} className="loginSection-logo" alt="logo" />
          </div>
          <br />
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h3 style={{ margin: 0, color: "#333" }}>{stepTitle[step]}</h3>
            <p style={{ color: "#666", fontSize: 14, marginTop: 8 }}>
              {stepDescription[step]}
            </p>
          </div>

          {step === "email" && (
            <Form
              name="forgotPassword"
              onFinish={handleSendOTP}
              autoComplete="off"
            >
              <div>E-mail</div>
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please input your email!",
                  },
                  {
                    type: "email",
                    message: "Please enter a valid email address!",
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter your registered email"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  block
                  style={{ height: 50 }}
                >
                  Send OTP
                </Button>
              </Form.Item>
              <div style={{ textAlign: "center" }}>
                <a
                  onClick={() => navigate("/")}
                  style={{ color: "#0055A6", cursor: "pointer" }}
                >
                  Back to Login
                </a>
              </div>
            </Form>
          )}

          {step === "otp" && (
            <Form
              name="verifyOTP"
              onFinish={handleVerifyOTP}
              autoComplete="off"
            >
              <div>OTP</div>
              <Form.Item
                name="otp"
                rules={[
                  {
                    required: true,
                    message: "Please enter the OTP!",
                  },
                ]}
              >
                <Input size="large" placeholder="Enter OTP from email" />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  block
                  style={{ height: 50 }}
                >
                  Verify OTP
                </Button>
              </Form.Item>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <a
                  onClick={() => setStep("email")}
                  style={{ color: "#0055A6", cursor: "pointer" }}
                >
                  Change Email
                </a>
                <a
                  onClick={() => navigate("/")}
                  style={{ color: "#0055A6", cursor: "pointer" }}
                >
                  Back to Login
                </a>
              </div>
            </Form>
          )}

          {step === "password" && (
            <Form
              name="resetPassword"
              onFinish={handleResetPassword}
              autoComplete="off"
            >
              <div>New Password</div>
              <Form.Item
                name="newPassword"
                rules={[{ validator: validatePassword }]}
                style={passwordValue && !allPasswordRulesPassed ? { marginBottom: 4 } : {}}
                help={passwordValue && !allPasswordRulesPassed ? "" : undefined}
              >
                <Input.Password
                  size="large"
                  placeholder="Enter new password"
                  onChange={(e) => setPasswordValue(e.target.value)}
                />
              </Form.Item>
              {passwordValue && !allPasswordRulesPassed && (
                <div style={{ marginBottom: 12 }}>
                  {passwordRules.map((rule, i) => {
                    const passed = rule.test(passwordValue);
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "2px 0",
                          fontSize: 13,
                          color: passed ? "#52c41a" : "#999",
                        }}
                      >
                        <span style={{ fontSize: 14 }}>{passed ? "\u2713" : "\u2715"}</span>
                        {rule.label}
                      </div>
                    );
                  })}
                </div>
              )}
              <div>Confirm Password</div>
              <Form.Item
                name="confirmPassword"
                dependencies={["newPassword"]}
                rules={[
                  {
                    required: true,
                    message: "Please confirm your password!",
                  },
                  ({ getFieldValue }: any) => ({
                    validator(_: any, value: any) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject("Passwords do not match!");
                    },
                  }),
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder="Confirm new password"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={!allPasswordRulesPassed}
                  size="large"
                  block
                  style={{ height: 50 }}
                >
                  Reset Password
                </Button>
              </Form.Item>
              <div style={{ textAlign: "center" }}>
                <a
                  onClick={() => navigate("/")}
                  style={{ color: "#0055A6", cursor: "pointer" }}
                >
                  Back to Login
                </a>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
